// What's new: a short note for each release that people will notice, shown once after the update
// arrives. Add a release here only for changes a user would notice (a new feature, a visible fix). Changes
// behind the scenes get no note, so no card appears for them.
import { APP_NAME_AR } from './brand';
import { HELP, N, TASKS, type Flash } from './guide';
import { EVIDENCE } from './islamEvidence';
import { WHY_NAME } from './name';

export type NewsTarget = 'nights' | 'layers' | 'settings' | 'help' | 'tonight' | 'islam' | 'calendar' | 'gallery' | 'sizes' | 'name' | 'learn' | 'more';
/** Ahillah's films (src/ui/IntroFilm.tsx). */
export type Film = 'intro' | 'calendar';
/** `show`: the part of Ahillah the note's button opens, and `flash` the part of it lit up there, as Help's Show me does.
 *  `watch`: a note about a film, whose button plays it. */
export interface NewsItem { icon: string; title: string; text: string; show?: NewsTarget; flash?: Flash; watch?: Film }
/** A photograph on a page of an update's story: a Gallery picture by its id, with the credit the Gallery gives it. */
export interface StoryPhoto { id: string; alt: string; credit: string }
/**
 * A page of an update's story (src/ui/UpdateStory.tsx): a picture, a heading, a few lines, and what to try.
 * `art`: 'name', the name over a photograph; 'photos', one to four photographs; 'sizes', worlds from "How big are they?"
 * at their true sizes (`sizes`, by id); 'dock', the bar along the bottom as it is now; 'verse', a verse of the Quran
 * (`quran`, with its Arabic reference) set as in MuSunnah's books; 'icon', Ahillah's icon. `points`: short points with icons.
 */
export interface StoryPage {
  art: 'name' | 'photos' | 'sizes' | 'dock' | 'verse' | 'icon';
  quran?: { arabic: string; refAr: string };
  title: string;
  text: string;
  photos?: StoryPhoto[];
  sizes?: string[];
  points?: { icon: string; text: string }[];
  show?: NewsTarget;
}
/**
 * `film`: a film that tells this release's news, offered in place of the card (the card follows if it is declined).
 * `replay`: an earlier release's film, offered first again with this one; this release's notes follow on the card
 * whether or not it is watched.
 * `story`: pages that tell this release's news in full, shown in place of the card once, and again from the list of
 * updates. The notes (`items`) say the same in short, for the list.
 */
export interface Release { id: string; date: string; items: NewsItem[]; film?: 'calendar'; replay?: 'calendar'; story?: StoryPage[] }

const AR = `\u2068${APP_NAME_AR}\u2069`;

/** A verse on a page of a story: its Arabic as the King Fahd Complex sets it, and its English with the reference. */
const verse = (id: string, refAr: string) => ({ quran: { arabic: EVIDENCE[id].arabic, refAr }, english: `“${EVIDENCE[id].english}” ${EVIDENCE[id].ref}` });
const STARS_VERSE = verse('Q-44', '[الصافات: ٦]');
const STATIONS_VERSE = verse('Q-49', '[يس: ٣٩]');

/** Newest first. The id is the release date (YYYY-MM-DD, with .2 and so on for a second release that day) and orders them. */
export const RELEASES: Release[] = [
  {
    // The release day: set both to the day it goes live. The owner's message first, as he asked (24 October 2026): Fable's
    // longer draft, closest to his own words; then feedback (DECISIONS 112) and Align (DECISIONS 111), Sonnet's drafts.
    id: '2026-10-24',
    date: '24 October 2026',
    items: [
      { icon: 'sparkle', title: 'A word from us', text: 'Over the next few days and weeks we’re working to make Ahillah better, above all more accurate. Along the way you may find things added, changed or taken away. We ask for your patience, and any feedback you can give us will be extremely useful.' },
      { icon: 'message', title: 'Tell us what you think', text: `Spotted something, or got an idea? Tap the question mark (?) at the top, then “${HELP.feedback}”, pick what you looked at and add a few words, with no name or email.`, show: 'help', flash: { text: HELP.feedback, row: '.result' } },
      { icon: 'target', title: 'Match the real sky', text: `Does the sky on your screen match the real one? While pointing your phone, tap “${N.align}”, find the Moon or a bright star Ahillah picks, put the circle over it, then tap “${N.linedUp}”.`, show: 'help', flash: { text: TASKS.find((t) => t.id === 'align')!.title, row: '.howto' } },
    ],
  },
  {
    // The owner's choice of 23 October 2026 from three drafts: Claude's (DECISIONS 109).
    id: '2026-10-23.3',
    date: '23 October 2026',
    items: [
      { icon: 'play', title: 'Listen to the Quran', text: 'Tap the play button under any verse to hear it recited by al\u2011Minshāwī, and hear the scholars’ tafsir read in full.', show: 'islam' },
    ],
  },
  {
    // The release day: set both to the day it goes live (with .2 on the same day as the last). The owner's choices of
    // 23 October 2026 from three drafts each: Sonnet's, with the scholars' words added (DECISIONS 107), and Sonnet's
    // for More (DECISIONS 108).
    id: '2026-10-23.2',
    date: '23 October 2026',
    items: [
      { icon: 'speaker', title: 'Hear the sky in Arabic', text: 'How do you say that star’s name? Tap the little speaker beside an Arabic name, a hadith or the scholars’ words and listen, then choose a man’s or a woman’s voice in Settings.', show: 'settings', flash: { text: N.arabicVoice, row: '.seg-row' } },
      { icon: 'grid', title: 'More to explore', text: 'Fancy a quiz, a guided tour or a Sky Hunt? You’ll find them in More, at the bottom right, with lessons, The stars in Islam and the sky calendar.', show: 'more' },
    ],
  },
  {
    // The words are the owner's choice of 22 October 2026 from three drafts (Claude's, Sonnet's and Fable's): DECISIONS 100.
    id: '2026-10-23',
    date: '23 October 2026',
    items: [
      { icon: 'moon', title: 'Hijri date, crescent alerts', text: 'You’ll find the Hijri date beside the date in Tonight, with alerts for the crescents of Ramaḍān, Shawwāl and Dhū\u00a0al\u2011Ḥijjah.', show: 'tonight' },
      { icon: 'book', title: 'The stars in Islam', text: 'Read what the Quran and the Sunnah say about the sky, and look out for notes on the white days, Ramaḍān, ʿArafah and ʿĀshūrāʾ as they come.', show: 'islam' },
      { icon: 'moon', title: 'How a month begins', text: '“The crescent and the Hijri month” is a new lesson in Learn, with where to look for the crescent yourself.', show: 'learn' },
      { icon: 'star', title: 'Read the sky in Arabic', text: 'Explore the Arabian star groups and the Moon’s 28 stations in Sky view, and, with Arabic names on, read the hadith and scholars’ words with full ḥarakāt.', show: 'settings' },
      { icon: 'info', title: 'The little things', text: 'A new icon, silent alerts, the sky’s glow in Naked eye, 61 more star names and twilight without a red band.' },
    ],
    story: [
      {
        art: 'photos',
        title: 'What’s the Hijri date today?',
        text: 'You’ll find it beside the date in Tonight, and in the sky calendar. It turns at sunset, when the Islamic day begins. And on the 29th before Ramaḍān, Shawwāl, Dhū\u00a0al\u2011Ḥijjah and Muḥarram, it waits for the official announcement.',
        photos: [{ id: 'moon-mccarthy-gigamoon-2023', alt: 'The Moon, more than half lit, its craters sharp and its plains tinted blue and brown', credit: 'Andrew McCarthy (cropped)' }],
        show: 'tonight',
      },
      {
        art: 'photos',
        title: 'Be ready for the crescent',
        text: 'With notifications on, you’ll get an alert on the 29th when the crescent of Ramaḍān, Shawwāl or Dhū\u00a0al\u2011Ḥijjah is being looked for, and another with the result. Turn them off in Settings, under Alerts. Saw it? Save it in Your nights, with the duʿāʾ.',
        photos: [{ id: 'moon-crescent-venus-mercury-paranal-2011', alt: 'A thin crescent Moon low over dark mountains at dusk, with Venus and Mercury above it', credit: 'ESO/B. Tafreshi (twanight.org)' }],
        show: 'settings',
      },
      {
        art: 'verse',
        title: 'The stars in Islam',
        quran: STARS_VERSE.quran,
        text: `${STARS_VERSE.english}\n\nWhat do the Quran and the Sunnah say about the sky, astrology and star signs? Find out near the top of Tonight, with explanations from Shaykh Ibn Bāz ﵀, Shaykh Ibn ʿUthaymīn ﵀ and others. Verses on the Sun and Moon cards too.`,
        show: 'islam',
      },
      {
        art: 'photos',
        title: 'Throughout the year',
        text: 'Each appears right when it matters:',
        photos: [{ id: 'moon-eclipse-2022', alt: 'The Moon glowing a dull red during a total lunar eclipse', credit: 'NASA/Joel Kowsky' }],
        points: [
          { icon: 'moon', text: 'A note as the white days near, and a hadith on the night of the full Moon.' },
          { icon: 'tonight', text: 'Ramaḍān in Tonight: when it begins, each night’s Moon and the last ten nights.' },
          { icon: 'calendar', text: 'ʿArafah and ʿĀshūrāʾ in the sky calendar, with notes and a reminder you can set.' },
          { icon: 'sun', text: 'The eclipse prayer on the Sun and Moon cards while an eclipse can be seen.' },
          { icon: 'meteor', text: 'Shooting stars: the verse, explained by Ibn Kathīr ﵀, on every meteor shower.' },
          { icon: 'star', text: 'The hadith on al\u2011Thurayyā, the Pleiades, right on its card.' },
        ],
      },
      {
        art: 'verse',
        title: 'Learn how a month begins',
        quran: STATIONS_VERSE.quran,
        text: `${STATIONS_VERSE.english}\n\n“The crescent and the Hijri month”: about three minutes on tonight’s real sky, with where to look for the crescent yourself. “The Moon’s phases” gains a verse too.`,
        show: 'learn',
      },
      {
        art: 'photos',
        title: 'Read the sky in Arabic',
        text: 'Sky view now has the Arabian star groups and the Moon’s 28 stations. Switch on Arabic names in Settings and the stars, planets and more show their Arabic names, in search too. The hadith and the scholars’ quotes appear in Arabic, now with full ḥarakāt.',
        photos: [{ id: 'milky-way-armazones-horalek-2019', alt: 'The Milky Way arching over a dark desert mountain top', credit: 'ESO/P. Horálek' }],
        show: 'settings',
      },
      {
        art: 'icon',
        title: 'The little things',
        text: 'Small, but worth a look:',
        points: [
          { icon: 'sparkle', text: 'New icon: a thin crescent at dusk. On an iPhone or iPad, Help shows how to get it.' },
          { icon: 'bell', text: 'Alerts are silent now. To hear them, turn on “Play a sound” in Settings.' },
          { icon: 'eye', text: 'In Naked eye, the sky now glows low down and near the Moon.' },
          { icon: 'star', text: 'Hear facts about 18 bright stars with Listen, plus 61 more official star names.' },
          { icon: 'sun', text: 'Twilight now blends into night, with no red band.' },
        ],
      },
    ],
  },
  {
    id: '2026-10-08',
    date: '8 October 2026',
    items: [
      { icon: 'moon', title: 'A new name: Ahillah', text: `Ahillah (${AR}) means new crescents [Al\u2011Baqarah:\u00a02:189]. It is the same app, at a new address: ahillah.pages.dev.`, show: 'name' },
      { icon: 'gallery', title: 'The Gallery', text: '679 real photographs of space: every planet through the years, with Saturn’s rings, Jupiter’s Great Red Spot and more; the large moons, the dwarf planets, the Sun, comets and asteroids; and the finest from Webb and Hubble.', show: 'gallery' },
      { icon: 'sizes', title: 'How big are they?', text: 'The planets, the large moons and the dwarf planets beside Earth at their true sizes, made from real photographs.', show: 'sizes' },
      { icon: 'info', title: 'A few smaller changes', text: 'Planes and “What was that?” have gone, so Ahillah keeps to the sky; nights saved with planes in them still open. Where to go gives each place’s distance in a straight line, with Directions for the way there.' },
    ],
    story: [
      {
        art: 'name',
        title: 'A new name: Ahillah',
        text: `Ahillah (${AR}) means new crescents, the word the Quran uses for them [Al\u2011Baqarah:\u00a02:189]. It is the same app, at a new address, ahillah.pages.dev, and everything you saved came with you.`,
        photos: [{ id: 'moon-crescent-venus-mercury-paranal-2011', alt: 'A thin crescent Moon low over dark mountains at dusk, with Venus and Mercury above it', credit: 'ESO/B. Tafreshi (twanight.org)' }],
      },
      {
        art: 'verse',
        title: 'Why Ahillah?',
        quran: { arabic: WHY_NAME.verse.arabic, refAr: WHY_NAME.verse.refAr },
        text: `“${WHY_NAME.verse.english}” ${WHY_NAME.verse.ref}\n\n${WHY_NAME.quotes[0].lead} “${WHY_NAME.quotes[0].english}”`,
        show: 'name',
      },
      {
        art: 'photos',
        title: 'The Gallery',
        text: '679 real photographs of space. Every planet has a page of its own, through the years and with its notable features: Saturn’s rings, Jupiter’s Great Red Spot and more. And the finest from Webb and Hubble.',
        photos: [
          { id: 'saturn-cassini-portrait-2004', alt: 'Saturn and its rings, from Cassini', credit: 'NASA/JPL/Space Science Institute' },
          { id: 'jupiter-voyager1-great-red-spot-1979', alt: 'Jupiter’s Great Red Spot, from Voyager 1', credit: 'NASA/JPL' },
          { id: 'webb-eagle-pillars', alt: 'Pillars of gas and dust in the Eagle Nebula, from the Webb telescope', credit: 'NASA, ESA, CSA, STScI; J. DePasquale, A. Koekemoer, A. Pagan (STScI).' },
        ],
        show: 'gallery',
      },
      {
        art: 'photos',
        title: 'Moons, dwarf planets and more',
        text: 'The large moons and the dwarf planets each have a page too, with their facts and their best photographs. So do the Sun, comets and asteroids.',
        photos: [
          { id: 'jupiter-galileo-io-1999', alt: 'Jupiter’s moon Io, yellow and spotted with volcanoes, from Galileo', credit: 'NASA/JPL/University of Arizona' },
          { id: 'jupiter-galileo-europa-1998', alt: 'Jupiter’s moon Europa, its ice crossed by reddish lines, from Galileo', credit: 'NASA/JPL-Caltech/SETI Institute' },
          { id: 'dwarf-ceres-whole-dawn', alt: 'The grey, cratered dwarf planet Ceres, from Dawn', credit: 'NASA/JPL-Caltech/UCLA/MPS/DLR/IDA' },
          { id: 'pluto-new-horizons-enhanced-colour-2015', alt: 'The dwarf planet Pluto with its pale heart-shaped plain, from New Horizons', credit: 'NASA/Johns Hopkins University Applied Physics Laboratory/Southwest Research Institute' },
        ],
        show: 'gallery',
      },
      {
        art: 'sizes',
        title: 'How big are they?',
        text: 'See the planets, the large moons and the dwarf planets beside Earth at their true sizes, made from real photographs. Earth would fit across Jupiter about eleven times.',
        sizes: ['jupiter', 'earth', 'moon'],
        show: 'sizes',
      },
      {
        art: 'dock',
        title: 'A few smaller changes',
        text: 'To keep Ahillah simple and focused on the sky:',
        points: [
          { icon: 'check', text: 'Planes and “What was that?” have gone.' },
          { icon: 'nights', text: 'Nights you saved with planes in them still open, without the planes.' },
          { icon: 'pin', text: 'Where to go gives each place’s distance in a straight line, with Directions for the way there.' },
        ],
      },
    ],
  },
  {
    id: '2026-10-03',
    date: '3 October 2026',
    // The owner asked for the sky calendar film to open this update too, for everyone.
    replay: 'calendar',
    items: [
      { icon: 'eye', title: 'See just what your eyes can see', text: 'In Sky view, choose Naked eye and Ahillah shows only what your eyes can see from where you are tonight, with names, so the stars you spot are easy to find. After dark, the darkness check fits it to your eyes.', show: 'layers' },
      { icon: 'phone', title: 'Steadier pointing', text: 'On iPhone, the sky no longer swings round when you tilt the phone up to look overhead, and big corrections now glide into place instead of jumping.' },
      { icon: 'bell', title: 'Reminders you can count on', text: 'A reminder you set with Remind me now arrives whatever the weather, saying if cloud is forecast, and is tried again if sending fails. Set one late and it comes an hour before the best time, or in a few minutes.', show: 'calendar' },
    ],
  },
  {
    // Its film was offered first after that update, and again with 3 October's (its replay).
    id: '2026-10-01.3',
    date: '1 October 2026',
    film: 'calendar',
    items: [
      { icon: 'calendar', title: 'Sky calendar', text: 'Meteor showers, eclipses, the planets and the new crescent for the year ahead, worked out for where you are or for any place you choose. Tap Remind me for a notification before it happens.', show: 'calendar' },
      { icon: 'meteor', title: 'What’s on, at a glance', text: 'When a meteor shower is under way, an eclipse is near or the crescent of Ramaḍān is due, a slim line over the sky says so. Tap it for the details.' },
      { icon: 'bell', title: 'Sky event notifications', text: 'If you have notifications on, Ahillah now also tells you before major sky events, such as a meteor shower’s peak, an eclipse you can see and the crescent of Ramaḍān, Shawwāl and Dhū al-Ḥijjah. You can turn this off in Settings, under Sky events.', show: 'settings' },
    ],
  },
  {
    // Shown on the card after the film, which does not mention these.
    id: '2026-10-01.2',
    date: '1 October 2026',
    items: [
      { icon: 'help', title: 'How to use Ahillah', text: 'A one-minute tour of the buttons, and quick answers to everyday questions, such as how to find a planet or set a reminder. Tap the ? at the top, or find Help in Settings.', show: 'help' },
      { icon: 'layers', title: 'Layers is now Sky view', text: 'The button for choosing what the sky shows, and Night vision, has a clearer name. Everything in it is where it was.', show: 'layers' },
    ],
  },
  {
    id: '2026-10-01',
    date: '1 October 2026',
    items: [
      { icon: 'info', title: 'The stars in Islam, revised', text: 'The scholars now speak in their own words, quoted from their books, and every verse and hadith has a new translation. Arabic names follow one spelling throughout.', show: 'islam' },
    ],
  },
  {
    id: '2026-09-30.4',
    date: '30 September 2026',
    items: [
      { icon: 'info', title: 'Reminders from Islam', text: 'Where Ahillah shows the zodiac, or a name from the false deities and myths of old, a short reminder now says what Islam teaches about it. The evidence is in “The stars in Islam”, also in Help.', show: 'islam' },
      { icon: 'eye', title: 'Any mix on the sky', text: 'In Sky view, choose constellations, star names, galaxies, satellites, aircraft and meteor showers in any mix, or Everything at once. Seeing through the ground and practice meteors work properly now too.', show: 'layers' },
      { icon: 'chevronDown', title: 'Make a card small', text: 'Tap the arrow on a card, or pull it down, to shrink it to a slim bar and keep the sky clear. Pointing at the sky, what you pick in Search opens small, with its guide.' },
    ],
  },
  {
    id: '2026-09-30.3',
    date: '30 September 2026',
    items: [
      { icon: 'speaker', title: 'Facts you can listen to', text: 'Tap Listen on a card and George tells you a few checked facts about it. There are over 500, covering the planets, named stars, constellations, Messier objects, meteor showers and satellites.' },
      { icon: 'check', title: 'More accurate cards', text: 'Globular clusters now show their brightness from the standard catalogue, and some wrong star distances, star types and names are fixed. For example, Messier 11 is now the Wild Duck Cluster.' },
    ],
  },
  {
    id: '2026-09-30.2',
    date: '30 September 2026',
    items: [
      { icon: 'nightEye', title: 'Gentler night vision', text: 'Night vision now warms the screen’s colours instead of turning them red, with one slider from less warm to more warm, like the iPhone’s Night Shift. It’s in Sky view.', show: 'layers' },
    ],
  },
  {
    id: '2026-09-30',
    date: '30 September 2026',
    items: [
      { icon: 'nights', title: 'Your nights', text: 'Tick “Seen it” on anything you spot and it’s saved to tonight. Later, relive the night: the whole sky as it was, to point your phone around again.', show: 'nights' },
    ],
  },
  {
    id: '2026-09-29',
    date: '29 September 2026',
    items: [
      { icon: 'phone', title: 'Point to explore', text: 'On a phone, Ahillah follows where you hold it from the moment it opens. Drag with a finger whenever you like; move the phone and it follows again.' },
      { icon: 'eye', title: 'A cleaner sky', text: 'The sky starts with the stars, planets and the Moon. Add constellations, satellites and more in Sky view.', show: 'layers' },
      { icon: 'speaker', title: 'A new narration voice', text: 'George reads the tours and the names in a warmer, more natural voice.' },
      { icon: 'star', title: 'Arabic by choice', text: 'Arabic star names and the Moon’s stations are one setting now, in Settings › Arabic.', show: 'settings' },
      { icon: 'play', title: 'A short introduction', text: 'A film about Ahillah, in Help.', show: 'help', watch: 'intro' },
    ],
  },
];

export const LATEST = RELEASES[0].id;

/** The notes someone has not seen yet (newest first, at most `max`), given the last release they saw. */
export function unseenNews(lastSeen: string | null, max = 3, skip?: string): { items: NewsItem[]; more: number } {
  const items = RELEASES.filter((r) => (!lastSeen || r.id > lastSeen) && r.id !== skip).flatMap((r) => r.items.filter(noteShown));
  return { items: items.slice(0, max), more: Math.max(0, items.length - max) };
}

/** What a note's button says for each part of Ahillah it opens, so people learn where things live. */
export const NEWS_ACTION: Record<NewsTarget, string> = {
  nights: 'See your nights',
  layers: 'Open Sky view',
  settings: 'Open Settings',
  help: 'Open Help',
  tonight: 'Open Tonight',
  islam: 'Read “The stars in Islam”',
  calendar: 'Open the sky calendar',
  gallery: 'Open the Gallery',
  sizes: 'See how big they are',
  name: 'Read why it is called Ahillah',
  learn: 'Open Learn',
  more: 'Open More',
};

/**
 * Each film as the list of updates offers it. `offered`: false while a film is parked, so nothing offers it (the
 * welcome, Help, the card or the list of updates) and notes about it are left out. Both are parked since 8 October
 * 2026: the sky calendar film still says the app's first name, and the introduction is being made again.
 */
export const FILMS: Record<Film, { name: string; length: string; watch: string; offered: boolean }> = {
  intro: { name: 'Ahillah', length: '65 seconds, with sound', watch: 'Watch the introduction', offered: false },
  calendar: { name: 'The sky calendar', length: '74 seconds, with sound', watch: 'Watch the film', offered: false },
};

/** Whether a note shows: one about a film shows only while the film is offered. */
export const noteShown = (it: NewsItem) => !it.watch || FILMS[it.watch].offered;

/** A note's button: it plays the note's film, or opens the part of Ahillah the note is about. None for a note that needs neither. */
export function noteAction(it: NewsItem): { label: string; film: Film } | { label: string; show: NewsTarget } | null {
  if (it.watch) return { label: FILMS[it.watch].watch, film: it.watch };
  if (it.show) return { label: NEWS_ACTION[it.show], show: it.show };
  return null;
}

/** The history of updates a day at a time, newest first: the day's notes (from all its releases), any film that came
 * with them, and the releases told in pages (`stories`, by id). */
export interface NewsDay { date: string; films: Film[]; items: NewsItem[]; stories: string[] }
export function newsDays(releases: Release[] = RELEASES): NewsDay[] {
  const days: NewsDay[] = [];
  for (const r of releases) {
    let day = days[days.length - 1];
    if (day?.date !== r.date) days.push((day = { date: r.date, films: [], items: [], stories: [] }));
    if (r.film && FILMS[r.film].offered) day.films.push(r.film);
    if (r.story?.length) day.stories.push(r.id);
    day.items.push(...r.items.filter(noteShown));
  }
  return days;
}
